import { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { RiFolderUploadLine } from "react-icons/ri";
import { FiTrash2, FiSearch, FiEdit2, FiChevronDown, FiChevronLeft, FiChevronRight, FiX } from "react-icons/fi";
import { TbReplace } from "react-icons/tb";
import { BsThreeDotsVertical } from "react-icons/bs";

import { FaFilePdf, FaFileWord } from "react-icons/fa6";
import { IoDocumentText, IoFilterSharp } from "react-icons/io5";
import { FaFileAlt, FaFileExcel } from "react-icons/fa";
import moment from "moment/dist/moment.js";

import UploadModal from "../../components/UploadModal";
import UpdateDocModal from "../../components/UpdateDocModal";
import DeleteDocModal from "../../components/DeleteDocModal";
import EditDocumentModal from "../../components/EditDocumentModal";
import ENDPOINTS from "../../api/endpoint";
import { showErrorToast, showSuccessToast } from "../../components/Toast";
import Loader from "../../components/Loader";

export default function Docs() {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);

  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [deleteType, setDeleteType] = useState(""); // 'single' or 'all'
  const [fileToDelete, setFileToDelete] = useState(null);
  const [fileToUpdate, setFileToUpdate] = useState(null);
  const [fileToEdit, setFileToEdit] = useState(null);
  const [userRole, setUserRole] = useState("");
  const [filterText, setFilterText] = useState("");
  const [openActionsId, setOpenActionsId] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState(null);
  const [filterChatbot, setFilterChatbot] = useState(""); // "" = all, or chatbot_id
  const [sortOrder, setSortOrder] = useState("desc"); // "asc" | "desc" (oldest first / newest first)
  const [nameSort, setNameSort] = useState(""); // "" | "a-z" | "z-a"
  const [showFiltersPanel, setShowFiltersPanel] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalDocuments, setTotalDocuments] = useState(0);
  const [selectedFileIds, setSelectedFileIds] = useState([]);
  const [selectionMode, setSelectionMode] = useState(false);

  const handleResetFilters = () => {
    setFilterChatbot("");
    setSortOrder("desc");
    setNameSort("");
  };

  const toggleSelectAllOnPage = () => {
    const visibleIds = files.map((f) => f.id);
    if (visibleIds.length === 0) return;
    setSelectionMode(true);
    setSelectedFileIds((prev) => {
      const visibleSet = new Set(visibleIds);
      const allSelected = visibleIds.every((id) => prev.includes(id));
      if (allSelected) {
        // Unselect all visible and exit selection mode
        const next = prev.filter((id) => !visibleSet.has(id));
        if (next.length === 0) {
          setSelectionMode(false);
        }
        return next;
      }
      const next = new Set(prev);
      visibleIds.forEach((id) => next.add(id));
      return Array.from(next);
    });
  };

  const handleToggleFileSelection = (fileId) => {
    setSelectionMode(true);
    setSelectedFileIds((prev) => {
      if (prev.includes(fileId)) {
        const next = prev.filter((id) => id !== fileId);
        if (next.length === 0) {
          setSelectionMode(false);
        }
        return next;
      }
      return [...prev, fileId];
    });
  };

  const handleBulkDeleteClick = async () => {
    if (!selectedFileIds.length) return;

    const user = JSON.parse(localStorage.getItem("user")) || {};
    const token = user.access_token;
    const companyId = user.company_id;
    const userId = user.user_id || user.id;

    if (!companyId || !token || !userId) {
      showErrorToast("Missing authentication details");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(ENDPOINTS.BULK_DELETE_DOCUMENTS(companyId), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "X-User-Id": userId,
        },
        body: JSON.stringify({
          document_ids: selectedFileIds,
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        showErrorToast(
          data.detail || data.message || "Failed to delete selected documents"
        );
        return;
      }

      showSuccessToast("Selected documents deleted successfully");

      // Optimistically update local state
      setFiles((prev) => prev.filter((file) => !selectedFileIds.includes(file.id)));
      setTotalDocuments((prev) =>
        Math.max(0, prev - selectedFileIds.length)
      );
      setSelectedFileIds([]);
      setSelectionMode(false);

      // Refetch to stay in sync with backend
      fetchDocs();
    } catch (err) {
      console.error("Bulk delete error:", err);
      showErrorToast("Something went wrong while deleting documents");
    } finally {
      setLoading(false);
    }
  };

  const fetchDocs = useCallback(async (overridePage, overrideLimit) => {
    const pageNum = overridePage ?? page;
    const limitNum = overrideLimit ?? limit;
    const user = JSON.parse(localStorage.getItem("user")) || {};
    const token = user.access_token;
    const companyId = user.company_id;

    if (!companyId || !token) {
      showErrorToast("Missing authentication details");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(ENDPOINTS.GET_DOCUMENTS(companyId, pageNum, limitNum), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await res.json();

      if (!res.ok) {
        showErrorToast(data.detail || "Failed to fetch documents");
        return;
      }

      const total = data.total_documents ?? data.total ?? (data.documents?.length ?? 0);
      setTotalDocuments(total);

      // ✅ Map docs and include blob_url, dates
      const docs = (data.documents || []).map((doc) => ({
        id: doc.id,
        name: doc.name,
        status: doc.status || null,
        size: formatFileSize(doc.bytes || 0),
        type: doc.name.split(".").pop().toLowerCase(),
        blob_url: doc.blob_url || null,
        chatbot_name: doc.chatbot_name || "General",
        chatbot_id: doc.chatbot_id || null,
        created_at: doc.created_at || null,
        updated_at: doc.updated_at || null,
      }));

      setFiles(docs);
    } catch (err) {
      console.error("Error fetching docs:", err);
      showErrorToast("Something went wrong");
    } finally {
      setLoading(false);
    }
  }, [page, limit]);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user")) || {};
    setUserRole(user.role || "enduser"); // default non-admin
  }, []);

  useEffect(() => {
    fetchDocs();
  }, [fetchDocs]);

  // Clear selections when page changes
  useEffect(() => {
    setSelectedFileIds([]);
    setSelectionMode(false);
  }, [page, limit]);

  // Listen for chatbot-deleted event from Sidebar (refresh docs when chatbot is deleted)
  useEffect(() => {
    const handleRefreshDocuments = () => fetchDocs();
    window.addEventListener("refresh-documents", handleRefreshDocuments);
    return () => window.removeEventListener("refresh-documents", handleRefreshDocuments);
  }, [fetchDocs]);

  // Close actions dropdown when clicking outside or on scroll
  useEffect(() => {
    if (!openActionsId) return;
    const close = () => {
      setOpenActionsId(null);
      setDropdownPosition(null);
    };
    document.addEventListener("click", close);
    document.addEventListener("scroll", close, true);
    return () => {
      document.removeEventListener("click", close);
      document.removeEventListener("scroll", close, true);
    };
  }, [openActionsId]);

  const handleRowClick = (file) => {
    if (!file || !file.blob_url) {
      showErrorToast("Download link not available for this document");
      return;
    }
    // Open the blob URL in a new tab (browser handles download inline depending on file type)
    window.open(file.blob_url, "_blank", "noopener,noreferrer");
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 KB";
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
  };

  const formatDateDisplay = (file) => {
    const raw = file.updated_at || file.created_at;
    if (!raw) return "—";
    try {
      const localDate = moment.utc(raw).local();
      if (!localDate.isValid()) return "—";
      if (localDate.isSame(moment(), "day")) {
        return `Today at ${localDate.format("h:mm A")}`;
      }
      return localDate.format("MMM D, YYYY h:mm A");
    } catch {
      return "—";
    }
  };

  const getFileIcon = (type) => {
    switch (type) {
      case "pdf":
        return (
          <div className="flex items-center bg-red-200 p-2 rounded-lg">
            <FaFilePdf className="w-5 h-5 text-red-600" />
          </div>
        );
      case "docx":
      case "doc":
        return (
          <div className="flex items-center bg-blue-200 p-2 rounded-lg">
            <FaFileWord className="w-5 h-5 text-blue-600" />
          </div>
        );
      case "txt":
        return (
          <div className="flex items-center bg-gray-300 p-2 rounded-lg">
            <IoDocumentText className="w-5 h-5 text-gray-600" />
          </div>
        );
      case "jpg":
      case "jpeg":
      case "png":
        return (
          <div className="flex items-center bg-purple-200 p-2 rounded-lg">
            <FaFileAlt className="w-5 h-5 text-purple-600" />
          </div>
        );
      case "xls":
      case "xlsx":
        return (
          <div className="flex items-center bg-green-200 p-2 rounded-lg">
            <FaFileExcel className="w-5 h-5 text-green-600" />
          </div>
        );
      default:
        return (
          <div className="flex items-center bg-gray-200 p-2 rounded-lg">
            <FaFileAlt className="w-5 h-5 text-gray-600" />
          </div>
        );
    }
  };

  // 🗑️ Delete Handlers
  const openUploadModal = () => setIsUploadModalOpen(true);
  const closeUploadModal = () => {
    setIsUploadModalOpen(false);
  };

  const openUpdateModal = (file = null) => {
    if (!file) {
      showErrorToast("Please select a document from the list to update");
      return;
    }
    setFileToUpdate(file);
    setIsUpdateModalOpen(true);
  };
  const closeUpdateModal = () => {
    setIsUpdateModalOpen(false);
    setFileToUpdate(null);
  };

  const openEditModal = (file = null) => {
    if (!file) {
      showErrorToast("Please select a document from the list to edit");
      return;
    }
    if (!file.blob_url) {
      showErrorToast("Document URL not available for editing");
      return;
    }
    setFileToEdit(file);
    setIsEditModalOpen(true);
  };

  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setFileToEdit(null);
  };

  const handleUploadSuccess = () => {
    // Refresh docs after successful upload (go to page 1 and refetch)
    if (page !== 1) setPage(1);
    else fetchDocs(1, limit);
  };

  const handleUpdateSuccess = () => {
    // Refresh docs after successful update
    fetchDocs();
  };

  const handleDeleteFile = (fileId) => {
    setFileToDelete(fileId);
    setDeleteType("single");
    setIsDeleteModalOpen(true);
  };

  const handleClearAll = () => {
    setDeleteType("all");
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setFileToDelete(null);
    setDeleteType("");
  };

  const confirmDelete = async () => {
    const user = JSON.parse(localStorage.getItem("user")) || {};
    const token = user.access_token;
    const companyId = user.company_id;
    const userId = user.user_id || user.id;

    if (!companyId || !token) {
      showErrorToast("Missing authentication details");
      return;
    }

    // 👇 show loader + close modal first
    setLoading(true);
    closeDeleteModal();

    try {
      if (deleteType === "single" && fileToDelete) {
        const headers = {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          "X-User-Id": userId,
        };

        // user_id is now required in the request body
        const res = await fetch(
          ENDPOINTS.DELETE_DOCUMENT(companyId, fileToDelete),
          {
            method: "DELETE",
            headers,
            body: JSON.stringify({
              user_id: userId,
            }),
          }
        );

        if (!res.ok) {
          const data = await res.json();
          showErrorToast(data.detail || "Failed to delete document");
          return;
        }

        setFiles((prev) => prev.filter((file) => file.id !== fileToDelete));
        setTotalDocuments((prev) => Math.max(0, prev - 1));
        showSuccessToast("Document deleted successfully");
        // If current page is now empty and not page 1, go to previous page
        if (files.length <= 1 && page > 1) {
          setPage((p) => p - 1);
        } else {
          fetchDocs();
        }
      } else if (deleteType === "all") {
        const headers = {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          "X-User-Id": userId,
        };

        // user_id is now required in the request body
        const res = await fetch(ENDPOINTS.CLEAR_ALL_DOCUMENTS(companyId), {
          method: "DELETE",
          headers,
          body: JSON.stringify({
            user_id: userId,
          }),
        });

        if (!res.ok) {
          const data = await res.json();
          showErrorToast(data.detail || "Failed to clear documents");
          return;
        }

        setFiles([]);
        setTotalDocuments(0);
        showSuccessToast("All documents cleared");
      }
    } catch (err) {
      console.error("Delete error:", err);
      showErrorToast("Something went wrong while deleting");
    } finally {
      setLoading(false); // 👈 hide loader
    }
  };

  const getDeleteModalProps = () => {
    if (deleteType === "single") {
      return {
        title: "Delete File",
        message: "Are you sure you want to delete this file?",
      };
    } else if (deleteType === "all") {
      return {
        title: "Clear All Files",
        message: "Are you sure you want to delete all files?",
      };
    }
    return {};
  };

  if (loading && files.length === 0) return <Loader />;

  if (!loading && totalDocuments === 0) {
    return (
      <>
        <div className="flex items-center justify-center py-32 bg-bg-light dark:bg-bg-dark px-4">
          <div className="text-center w-full mx-auto space-y-10">
            <h1 className="text-4xl md:text-7xl font-bold text-indigo-900 dark:text-white mb-4">
              Your Documents
            </h1>
            <p className="text-xl md:text-4xl text-gray-700 dark:text-gray-300 mb-8">
              Upload files to get started
            </p>
            <div className="flex justify-center">
              <button
                onClick={openUploadModal}
                className="flex items-center bg-indigo-900 hover:bg-indigo-800 dark:bg-bg-light dark:hover:bg-indigo-600 text-white dark:text-text-dark px-8 py-4 rounded-full text-lg font-medium transition-colors shadow-lg"
              >
                <RiFolderUploadLine className="w-8 h-8 mr-3" />
                Upload a new doc
              </button>
            </div>
          </div>
        </div>
        <UploadModal
          isOpen={isUploadModalOpen}
          onClose={closeUploadModal}
          onUploadSuccess={handleUploadSuccess}
        />
      </>
    );
  }

  return (
    <>
      <div className="bg-bg-light dark:bg-bg-dark min-h-screen overflow-auto">
        <div className="px-6 md:px-10 py-8 md:py-20">
          {/* Header */}
          <div className="flex justify-between items-center mb-5">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                Uploaded Files
              </h1>
            </div>
            <div className="flex gap-4">
              <p className="text-gray-600 dark:text-text-light">
                {totalDocuments} files total
              </p>
              <button
                onClick={userRole === "admin" ? handleClearAll : null}
                disabled={userRole !== "admin"}
                className={`${userRole === "admin"
                  ? "text-text-dark hover:text-gray-800 dark:text-text-light dark:hover:text-indigo-600"
                  : "text-gray-400 cursor-not-allowed"
                  } text-sm font-medium`}
              >
                Clear All
              </button>
            </div>
          </div>
          <div className="w-full items-end justify-between flex mb-4 gap-4 flex-wrap">
            {/* Search */}
            <div className="flex-1 min-w-[200px] max-w-md relative">
              <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 pointer-events-none">
                <FiSearch className="w-5 h-5" />
              </div>
              <input
                type="text"
                placeholder="Search documents..."
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-transparent border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 transition-colors"
              />
            </div>
            {/* Action Buttons: Upload + Filter icon */}
            <div className="relative flex items-center gap-2">
              <button
                onClick={userRole === "admin" ? openUploadModal : null}
                disabled={userRole !== "admin"}
                className={`${userRole === "admin"
                  ? "bg-indigo-900 hover:bg-indigo-800 text-white"
                  : "bg-gray-400 text-gray-200 cursor-not-allowed"
                  } px-4 py-3 rounded-full text-sm font-medium transition-colors`}
              >
                Upload a new doc
              </button>
              <button
                type="button"
                onClick={() => setShowFiltersPanel((v) => !v)}
                className={`p-3 rounded-full border transition-colors ${showFiltersPanel
                  ? "bg-indigo-100 dark:bg-indigo-900/30 border-indigo-500 text-indigo-600 dark:text-indigo-400"
                  : "border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                  }`}
                title="Filters"
              >
                <IoFilterSharp className="w-5 h-5" />
              </button>
              {showFiltersPanel && (
                <div className="absolute right-0 top-full mt-2 w-72 rounded-2xl bg-white dark:bg-gray-900 shadow-xl border border-gray-200 dark:border-gray-700 z-20">
                  <div className="flex items-center justify-between px-4 pt-3 pb-2 border-b border-gray-200 dark:border-gray-700">
                    <span className="text-xs font-semibold tracking-wide text-gray-500 dark:text-gray-400">
                      FILTERS
                    </span>
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={handleResetFilters}
                        className="text-xs font-semibold text-red-600 hover:text-red-700"
                      >
                        RESET
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowFiltersPanel(false)}
                        className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                        aria-label="Close filters"
                      >
                        <FiX className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <div className="px-4 py-3 space-y-3">
                    {/* Chatbot filter */}
                    <div className="space-y-1">
                      <p className="text-xs text-gray-500 dark:text-gray-400">Chatbot</p>
                      <div className="relative">
                        <select
                          value={filterChatbot}
                          onChange={(e) => setFilterChatbot(e.target.value)}
                          className="w-full py-2 pl-3 pr-8 appearance-none border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        >
                          <option value="">All</option>
                          {(() => {
                            const seen = new Set();
                            const options = [];
                            files.forEach((f) => {
                              const id = f.chatbot_id ?? "__general__";
                              if (seen.has(id)) return;
                              seen.add(id);
                              options.push({ value: id, label: f.chatbot_name || "General" });
                            });
                            options.sort((a, b) => a.label.localeCompare(b.label));
                            return options.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ));
                          })()}
                        </select>
                        <FiChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 dark:text-gray-400" />
                      </div>
                    </div>
                    {/* Time sort */}
                    <div className="space-y-1">
                      <p className="text-xs text-gray-500 dark:text-gray-400">Time</p>
                      <div className="relative">
                        <select
                          value={sortOrder}
                          onChange={(e) => setSortOrder(e.target.value)}
                          className="w-full py-2 pl-3 pr-8 appearance-none border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        >
                          <option value="desc">Newest first</option>
                          <option value="asc">Oldest first</option>
                        </select>
                        <FiChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 dark:text-gray-400" />
                      </div>
                    </div>
                    {/* Name sort: A to Z / Z to A */}
                    <div className="space-y-1">
                      <p className="text-xs text-gray-500 dark:text-gray-400">Name</p>
                      <div className="relative">
                        <select
                          value={nameSort}
                          onChange={(e) => setNameSort(e.target.value)}
                          className="w-full py-2 pl-3 pr-8 appearance-none border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        >
                          <option value="">None</option>
                          <option value="a-z">A to Z (Ascending)</option>
                          <option value="z-a">Z to A (Descending)</option>
                        </select>
                        <FiChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 dark:text-gray-400" />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Bulk selection bar (admin only) */}
          {userRole === "admin" && (
            <div className="flex items-center justify-between mb-2 px-1">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={selectionMode && files.length > 0 && files.every((f) => selectedFileIds.includes(f.id))}
                  onChange={toggleSelectAllOnPage}
                  className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">Select all</span>
                {selectionMode && files.some((f) => selectedFileIds.includes(f.id)) && (
                  <button
                    type="button"
                    onClick={handleBulkDeleteClick}
                    className="ml-2 inline-flex items-center justify-center rounded-full p-2 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/30"
                    title="Delete selected (log ids)"
                  >
                    <FiTrash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Files Table */}
          <div className="bg-bg-light dark:bg-gray-800 border border-gray-300 rounded-lg shadow-sm overflow-x-auto overflow-y-visible">
            {/* Table Header */}
            <div className="bg-indigo-900 dark:bg-bg-light px-6 py-4">
              <div className="grid grid-cols-12 gap-4 text-white dark:text-text-dark font-medium text-sm">
                {selectionMode && <div className="col-span-1" />}
                <div className={selectionMode ? "col-span-3" : "col-span-4"}>File Name</div>
                <div className="col-span-2">Chatbot</div>
                <div className="col-span-2">Status</div>
                <div className="col-span-2">Date</div>
                {selectionMode ? (
                  <>
                    <div className="col-span-1">Size</div>
                    <div className="col-span-1">Actions</div>
                  </>
                ) : (
                  <>
                    <div className="col-span-1">Size</div>
                    <div className="col-span-1">Actions</div>
                  </>
                )}
              </div>
            </div>

            {/* Table Body */}
            <div className="divide-y divide-gray-200 dark:divide-gray-300">
              {(() => {
                let filteredFiles = files.filter((file) =>
                  file.name.toLowerCase().includes(filterText.toLowerCase())
                );
                if (filterChatbot) {
                  filteredFiles = filteredFiles.filter((f) => (f.chatbot_id ?? "__general__") === filterChatbot);
                }
                const dateKey = (file) => {
                  const raw = file.updated_at || file.created_at;
                  return raw ? new Date(raw).getTime() : 0;
                };
                const nameCompare = (a, b, ascending) => {
                  const cmp = (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" });
                  return ascending ? cmp : -cmp;
                };
                filteredFiles = [...filteredFiles].sort((a, b) => {
                  if (nameSort === "a-z") {
                    const byName = nameCompare(a, b, true);
                    return byName !== 0 ? byName : dateKey(a) - dateKey(b);
                  }
                  if (nameSort === "z-a") {
                    const byName = nameCompare(a, b, false);
                    return byName !== 0 ? byName : dateKey(b) - dateKey(a);
                  }
                  const tA = dateKey(a);
                  const tB = dateKey(b);
                  return sortOrder === "desc" ? tB - tA : tA - tB;
                });

                if (filteredFiles.length === 0) {
                  return (
                    <div className="px-6 py-12 text-center">
                      <p className="text-gray-500 dark:text-gray-400 text-sm">
                        No Record Found
                      </p>
                    </div>
                  );
                }

                return filteredFiles.map((file) => (
                  <div
                    key={file.id}
                    onClick={() => handleRowClick(file)}
                    className="px-6 py-4 hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-bg-dark transition-colors cursor-pointer"
                  >
                    <div className="grid grid-cols-12 gap-4 items-center">
                      {/* Row selection checkbox */}
                      {selectionMode && (
                        <div className="col-span-1 flex items-center justify-center">
                          <input
                            type="checkbox"
                            checked={selectedFileIds.includes(file.id)}
                            onChange={() => handleToggleFileSelection(file.id)}
                            onClick={(e) => e.stopPropagation()}
                            className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          />
                        </div>
                      )}

                      {/* File Name */}
                      <div className={`${selectionMode ? "col-span-3" : "col-span-4"} flex items-center`}>
                        {getFileIcon(file.type)}
                        <span className="ml-3 text-gray-900 dark:text-white font-medium truncate">
                          {file.name}
                        </span>
                      </div>

                      {/* Chatbot */}
                      <div className="col-span-2 text-gray-600 dark:text-text-light text-sm">
                        {file.chatbot_name || "General"}
                      </div>

                      {/* Status */}
                      <div className="col-span-2 text-gray-600 dark:text-text-light text-sm">
                        {file.status || "—"}
                      </div>

                      {/* Date */}
                      <div className="col-span-2 text-gray-600 dark:text-text-light text-sm">
                        {formatDateDisplay(file)}
                      </div>

                      {/* Size */}
                      <div className="col-span-1 text-gray-600 dark:text-text-light text-sm">
                        {file.size}
                      </div>

                      {/* Actions */}
                      <div
                        className="col-span-1 flex gap-3 items-center"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {/* Three buttons: visible on md and up */}
                        <div className="hidden md:flex gap-3">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (userRole === "admin") {
                                openEditModal(file);
                              }
                            }}
                            disabled={userRole !== "admin"}
                            className={`${userRole === "admin"
                              ? "text-indigo-400 hover:text-indigo-600"
                              : "text-gray-400 cursor-not-allowed"
                              } transition-colors`}
                            title="Edit document"
                          >
                            <FiEdit2 className="w-5 h-5" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (userRole === "admin") {
                                openUpdateModal(file);
                              }
                            }}
                            disabled={userRole !== "admin"}
                            className={`${userRole === "admin"
                              ? "text-blue-400 hover:text-blue-600"
                              : "text-gray-400 cursor-not-allowed"
                              } transition-colors`}
                            title="Replace document"
                          >
                            <TbReplace className="w-5 h-5" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (userRole === "admin") {
                                handleDeleteFile(file.id);
                              }
                            }}
                            disabled={userRole !== "admin"}
                            className={`${userRole === "admin"
                              ? "text-red-400 hover:text-red-600"
                              : "text-gray-400 cursor-not-allowed"
                              } transition-colors`}
                            title="Delete document"
                          >
                            <FiTrash2 className="w-5 h-5" />
                          </button>
                        </div>

                        {/* Three-dots menu: visible on small screens only */}
                        <div className="relative md:hidden">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (openActionsId === file.id) {
                                setOpenActionsId(null);
                                setDropdownPosition(null);
                              } else {
                                const rect = e.currentTarget.getBoundingClientRect();
                                const w = 160;
                                const menuHeight = 130; // ~3 items with py-2.5
                                const gap = 4;
                                let left = rect.right - w;
                                if (left < 8) left = 8;
                                if (left + w > window.innerWidth - 8)
                                  left = window.innerWidth - w - 8;
                                // Flip above button if not enough space below
                                const spaceBelow = window.innerHeight - rect.bottom - gap;
                                let top =
                                  spaceBelow >= menuHeight
                                    ? rect.bottom + gap
                                    : rect.top - menuHeight - gap;
                                top = Math.max(8, Math.min(top, window.innerHeight - menuHeight - 8));
                                setDropdownPosition({ top, left });
                                setOpenActionsId(file.id);
                              }
                            }}
                            className="p-1.5 rounded-full text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                            title="Actions"
                          >
                            <BsThreeDotsVertical className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ));
              })()}
            </div>
          </div>

          {/* Pagination */}
          {totalDocuments > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-4 mt-4 px-2">
              <div className="flex items-center gap-4">
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  Showing {(page - 1) * limit + 1}–{Math.min(page * limit, totalDocuments)} of {totalDocuments}
                </span>
                <div className="flex items-center gap-2">
                  <label
                    htmlFor="docs-limit"
                    className="text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap"
                  >
                    Per page
                  </label>
                  <div className="relative">
                    <select
                      id="docs-limit"
                      value={limit}
                      onChange={(e) => {
                        setLimit(Number(e.target.value));
                        setPage(1);
                      }}
                      className="py-1.5 pl-2 pr-8 appearance-none border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                    </select>
                    <FiChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 dark:text-gray-400" />
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="py-2 px-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white dark:disabled:hover:bg-gray-800 transition-colors"
                  title="Previous page"
                  aria-label="Previous page"
                >
                  <FiChevronLeft className="w-5 h-5" />
                </button>
                <span className="text-sm text-gray-600 dark:text-gray-400 px-2">
                  Page {page} of {Math.max(1, Math.ceil(totalDocuments / limit))}
                </span>
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(Math.ceil(totalDocuments / limit), p + 1))}
                  disabled={page >= Math.ceil(totalDocuments / limit)}
                  className="py-2 px-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white dark:disabled:hover:bg-gray-800 transition-colors"
                  title="Next page"
                  aria-label="Next page"
                >
                  <FiChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Actions dropdown portal: renders over everything so not clipped by table overflow */}
      {openActionsId &&
        dropdownPosition &&
        createPortal(
          (() => {
            const file = files.find((f) => f.id === openActionsId);
            if (!file) return null;
            return (
              <div
                role="menu"
                onClick={(e) => e.stopPropagation()}
                className="fixed z-[9999] min-w-[160px] py-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-xl"
                style={{
                  top: dropdownPosition.top,
                  left: dropdownPosition.left,
                }}
              >
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (userRole === "admin") {
                      setOpenActionsId(null);
                      setDropdownPosition(null);
                      openEditModal(file);
                    }
                  }}
                  disabled={userRole !== "admin"}
                  className={`w-full flex items-center gap-2 px-4 py-2.5 text-left text-sm transition-colors ${userRole === "admin"
                    ? "text-indigo-600 dark:text-indigo-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                    : "text-gray-400 cursor-not-allowed"
                    }`}
                >
                  <FiEdit2 className="w-4 h-4 shrink-0" />
                  Edit
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (userRole === "admin") {
                      setOpenActionsId(null);
                      setDropdownPosition(null);
                      openUpdateModal(file);
                    }
                  }}
                  disabled={userRole !== "admin"}
                  className={`w-full flex items-center gap-2 px-4 py-2.5 text-left text-sm transition-colors ${userRole === "admin"
                    ? "text-blue-600 dark:text-blue-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                    : "text-gray-400 cursor-not-allowed"
                    }`}
                >
                  <TbReplace className="w-4 h-4 shrink-0" />
                  Replace
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (userRole === "admin") {
                      setOpenActionsId(null);
                      setDropdownPosition(null);
                      handleDeleteFile(file.id);
                    }
                  }}
                  disabled={userRole !== "admin"}
                  className={`w-full flex items-center gap-2 px-4 py-2.5 text-left text-sm transition-colors ${userRole === "admin"
                    ? "text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                    : "text-gray-400 cursor-not-allowed"
                    }`}
                >
                  <FiTrash2 className="w-4 h-4 shrink-0" />
                  Delete
                </button>
              </div>
            );
          })(),
          document.body
        )}

      {/* Update Modal */}
      <UpdateDocModal
        isOpen={isUpdateModalOpen}
        onClose={closeUpdateModal}
        onUpdateSuccess={handleUpdateSuccess}
        documentId={fileToUpdate?.id}
        documentName={fileToUpdate?.name}
        chatbotId={fileToUpdate?.chatbot_id ?? null}
      />

      {/* Upload Modal */}
      <UploadModal
        isOpen={isUploadModalOpen}
        onClose={closeUploadModal}
        onUploadSuccess={handleUploadSuccess}
      />

      {/* Delete Confirmation Modal */}
      <DeleteDocModal
        isOpen={isDeleteModalOpen}
        onClose={closeDeleteModal}
        onConfirm={confirmDelete}
        {...getDeleteModalProps()}
      />

      {/* Edit Document Modal */}
      <EditDocumentModal
        isOpen={isEditModalOpen}
        onClose={closeEditModal}
        file={fileToEdit}
      />
    </>
  );
}
